import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BookmarkSimple, FloppyDisk, X, Check } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { labelCls } from "@/lib/ui";

const ago = (iso) => {
  if (!iso) return "never used";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 3600) return `used ${Math.max(1, Math.round(d / 60))}m ago`;
  if (d < 86400) return `used ${Math.round(d / 3600)}h ago`;
  return `used ${Math.round(d / 86400)}d ago`;
};

const Chip = ({ b, active, onLoad, onDelete }) => {
  const [confirm, setConfirm] = useState(false);
  return (
    <div className={`group flex items-stretch border transition-colors ${active ? "border-[var(--o-fg)] bg-[var(--o-surface)]" : "border-[var(--o-border)] hover:border-[var(--o-border-strong)]"}`} data-testid={`briefing-chip-${b.id}`}>
      <button type="button" onClick={() => onLoad(b)} className="px-3 py-2 text-left" data-testid={`briefing-load-${b.id}`}>
        <div className="text-sm font-semibold leading-tight">{b.name}</div>
        <div className="font-mono text-[10px] text-[var(--o-muted2)] mt-0.5">{b.participants.length} people · {ago(b.used_at)}</div>
      </button>
      <button
        type="button"
        onClick={() => (confirm ? onDelete(b) : setConfirm(true))}
        onMouseLeave={() => setConfirm(false)}
        className={`px-2 border-l border-[var(--o-border)] transition-colors ${confirm ? "text-[#EF4444]" : "text-[var(--o-muted3)] hover:text-[var(--o-fg)]"}`}
        title={confirm ? "Click again to delete" : "Delete"}
        data-testid={`briefing-delete-${b.id}`}
      >
        {confirm ? <Check size={12} weight="bold" /> : <X size={12} />}
      </button>
    </div>
  );
};

export const BriefingLibrary = ({ form, onLoad, saveDisabled = false }) => {
  const [items, setItems] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");

  useEffect(() => {
    api.get("/briefings").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const load = async (b) => {
    const prev = form;
    onLoad({ title: b.title, purpose: b.purpose, participants: b.participants.length ? b.participants : [{ name: "", role: "", owns: "" }], briefing_csv: b.briefing_csv });
    setActiveId(b.id);
    toast.success(`Loaded "${b.name}"`, { action: { label: "Undo", onClick: () => { onLoad(prev); setActiveId(null); } } });
    api.post(`/briefings/${b.id}/use`).then(({ data }) => setItems((xs) => xs.map((x) => (x.id === data.id ? data : x)))).catch(() => {});
  };

  const remove = async (b) => {
    try {
      await api.delete(`/briefings/${b.id}`);
      setItems((xs) => xs.filter((x) => x.id !== b.id));
      if (activeId === b.id) setActiveId(null);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const save = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      const { data } = await api.post("/briefings", { name: n, ...form });
      setItems((xs) => [data, ...xs.filter((x) => x.id !== data.id)]);
      setActiveId(data.id);
      setSaving(false);
      toast.success(`Saved "${data.name}"`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const active = items.find((x) => x.id === activeId);
  const existing = items.find((x) => x.name.toLowerCase() === name.trim().toLowerCase());

  return (
    <div className="py-8 border-b border-[var(--o-border)] space-y-4" data-testid="briefing-library">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={`${labelCls} flex items-center gap-2`}><BookmarkSimple size={13} /> Saved briefings</span>
        {saving ? (
          <div className="flex items-center gap-2" data-testid="briefing-save-form">
            <input
              autoFocus
              autoComplete="off"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); save(); }
                if (e.key === "Escape") setSaving(false);
              }}
              placeholder="Name, e.g. Monthly leadership review"
              className="w-72 bg-[var(--o-bg)] border border-[var(--o-border)] px-3 py-2 text-sm outline-none focus:border-[var(--o-fg)]"
              data-testid="briefing-name-input"
            />
            <button type="button" onClick={save} disabled={!name.trim()} className="h-9 px-4 bg-[var(--o-fg)] text-[var(--o-bg)] text-sm font-semibold disabled:opacity-40" data-testid="briefing-save-confirm">
              {existing ? "Update" : "Save"}
            </button>
            <button type="button" onClick={() => setSaving(false)} className="h-9 px-2 text-[var(--o-muted2)] hover:text-[var(--o-fg)]" data-testid="briefing-save-cancel"><X size={14} /></button>
          </div>
        ) : saveDisabled ? (
          <span className="text-xs text-[var(--o-muted3)]" data-testid="briefing-save-disabled">Saving a briefing stores it on the server, so it is off for a private meeting.</span>
        ) : (
          <button
            type="button"
            onClick={() => { setName(active?.name || form.title || ""); setSaving(true); }}
            className="flex items-center gap-2 text-sm text-[var(--o-muted)] hover:text-[var(--o-fg)] transition-colors"
            data-testid="briefing-save-button"
          >
            <FloppyDisk size={14} /> {active ? `Update "${active.name}" or save as new` : "Save this briefing"}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--o-muted3)]" data-testid="briefing-library-empty">Fill the form below once, save it, and reload it in one click for recurring meetings.</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((b) => <Chip key={b.id} b={b} active={b.id === activeId} onLoad={load} onDelete={remove} />)}
        </div>
      )}
    </div>
  );
};
